/**
 * Horizontal scrollable strip of asset cards for a selected ad row.
 * Fetches ASSET entities via parentSourceEntityId and renders them
 * in a compact left-right swipeable layout.
 */
import React from 'react';
import Alert from '@amzn/meridian/alert';
import Column from '@amzn/meridian/column';
import MeridianLoader from '@amzn/meridian/loader';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import type { PublisherEntityDto } from 'src/store/generated/leela-api-generated';
import { usePublisherEntitiesControllerListForCampaignQuery } from 'src/store/leela-api';
import { compareByStatus } from 'src/utils/publisherEntityStatus';
import { EntityAssetCard } from './EntityAssetCard';

interface EntityAssetStripProps {
  adSourceEntityId: string | undefined;
  campaignId: string;
}

export const EntityAssetStrip: React.FC<EntityAssetStripProps> = ({
  adSourceEntityId,
  campaignId,
}) => {
  const { t } = useTranslation();
  const query = usePublisherEntitiesControllerListForCampaignQuery({
    campaignId,
    pageSize: 100,
    ...(adSourceEntityId != null ? { parentSourceEntityId: adSourceEntityId } : {}),
    publisher: 'google',
    publisherEntityType: 'ASSET',
  });

  const assets = (query.data?.data ?? []) as PublisherEntityDto[];

  if (query.isLoading) {
    return (
      <Row alignmentHorizontal="center" data-testid="ad-asset-strip-loading">
        <MeridianLoader size="small" />
      </Row>
    );
  }

  if (query.isError) {
    return (
      <Alert data-testid="ad-asset-strip-error" size="small" type="error">
        {t('ads_manager_ad_details_assets_error')}
      </Alert>
    );
  }

  if (assets.length === 0) {
    return (
      <Text color="secondary" data-testid="ad-asset-strip-empty">
        {t('ads_manager_ad_details_no_assets')}
      </Text>
    );
  }

  return (
    <Column data-testid="ad-asset-strip" spacing="200">
      <Text color="secondary" type="b200">{t('ads_manager_ad_details_assets')} ({assets.length})</Text>
      <div style={{ maxWidth: 'calc(100vw - 400px)', overflowX: 'auto', overflowY: 'hidden', paddingBottom: 8, WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'inline-flex', gap: 12 }}>
          {[...assets].sort(compareByStatus).map(asset => (
            <EntityAssetCard entity={asset} key={asset._id} />
          ))}
        </div>
      </div>
    </Column>
  );
};
